/**
 * Profile Page Component
 * Displays user information, agencies, and change password functionality
 * Uses OathUI components for consistent styling
 */

import { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import {
  UserCircleIcon,
  BuildingOfficeIcon,
  KeyIcon,
  EnvelopeIcon,
  PhoneIcon,
  EyeIcon,
  EyeSlashIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  PencilIcon,
  CheckIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { useOathAuth } from '../../../services/oathAuthContext';
import { getUserAgencies, updateOwnProfile } from '../../../services/oathUserService';
import { oathChangePassword, oathGetCurrentUser } from '../../../services/oathAuthService';
import {
  OathPageWrapper,
  OathHeaderCard,
  OathContentCard,
  OathButton,
  OathDualSpinner,
} from '../../shared/OathUI';

// Format phone number as (XXX) XXX-XXXX
const formatPhoneNumber = (value) => {
  if (!value) return '';
  const digits = value.replace(/\D/g, '');
  const limited = digits.slice(0, 10);
  if (limited.length <= 3) return limited;
  if (limited.length <= 6) return `(${limited.slice(0, 3)}) ${limited.slice(3)}`;
  return `(${limited.slice(0, 3)}) ${limited.slice(3, 6)}-${limited.slice(6)}`;
};

// Strip phone formatting for storage
const stripPhoneFormatting = (value) => {
  if (!value) return '';
  return value.replace(/\D/g, '');
};

const ProfilePage = () => {
  const { user, refreshSession } = useOathAuth();
  const [agencies, setAgencies] = useState([]);
  const [loadingAgencies, setLoadingAgencies] = useState(true);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [passwordError, setPasswordError] = useState('');
  const [changingPassword, setChangingPassword] = useState(false);
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false,
  });

  // Profile editing state
  const [isEditingProfile, setIsEditingProfile] = useState(false);
  const [profileForm, setProfileForm] = useState({
    firstName: '',
    lastName: '',
    phoneNumber: '',
  });
  const [profileError, setProfileError] = useState('');
  const [savingProfile, setSavingProfile] = useState(false);

  // Initialize profile form when user data is available
  useEffect(() => {
    if (user) {
      setProfileForm({
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        phoneNumber: formatPhoneNumber(user.phoneNumber || ''),
      });
    }
  }, [user]);

  useEffect(() => {
    const fetchAgencies = async () => {
      setLoadingAgencies(true);
      const result = await getUserAgencies();
      if (result.success) {
        setAgencies(result.agencies || []);
      }
      setLoadingAgencies(false);
    };

    fetchAgencies();
  }, []);

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordForm((prev) => ({ ...prev, [name]: value }));
    setPasswordError('');
  };

  const validatePassword = () => {
    if (!passwordForm.currentPassword) {
      setPasswordError('Current password is required');
      return false;
    }
    if (!passwordForm.newPassword) {
      setPasswordError('New password is required');
      return false;
    }
    if (passwordForm.newPassword.length < 8) {
      setPasswordError('New password must be at least 8 characters');
      return false;
    }
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setPasswordError('Passwords do not match');
      return false;
    }
    if (passwordForm.currentPassword === passwordForm.newPassword) {
      setPasswordError('New password must be different from current password');
      return false;
    }
    return true;
  };

  const handleSubmitPasswordChange = async (e) => {
    e.preventDefault();
    if (!validatePassword()) return;

    setChangingPassword(true);
    const result = await oathChangePassword(
      passwordForm.currentPassword,
      passwordForm.newPassword,
      passwordForm.confirmPassword
    );

    if (result.success) {
      toast.success('Password changed successfully');
      setShowChangePassword(false);
      setPasswordForm({
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
      });
    } else {
      setPasswordError(result.error || 'Failed to change password');
    }
    setChangingPassword(false);
  };

  const togglePasswordVisibility = (field) => {
    setShowPasswords((prev) => ({ ...prev, [field]: !prev[field] }));
  };

  // Profile editing handlers
  const handleProfileChange = (e) => {
    const { name, value } = e.target;
    if (name === 'phoneNumber') {
      setProfileForm((prev) => ({ ...prev, [name]: formatPhoneNumber(value) }));
    } else {
      setProfileForm((prev) => ({ ...prev, [name]: value }));
    }
    setProfileError('');
  };

  const handleEditProfile = () => {
    setProfileForm({
      firstName: user?.firstName || '',
      lastName: user?.lastName || '',
      phoneNumber: formatPhoneNumber(user?.phoneNumber || ''),
    });
    setProfileError('');
    setIsEditingProfile(true);
  };

  const handleCancelEdit = () => {
    setProfileForm({
      firstName: user?.firstName || '',
      lastName: user?.lastName || '',
      phoneNumber: formatPhoneNumber(user?.phoneNumber || ''),
    });
    setProfileError('');
    setIsEditingProfile(false);
  };

  const handleSaveProfile = async () => {
    // Validate
    if (!profileForm.firstName.trim()) {
      setProfileError('First name is required');
      return;
    }
    if (!profileForm.lastName.trim()) {
      setProfileError('Last name is required');
      return;
    }

    setSavingProfile(true);
    setProfileError('');

    const result = await updateOwnProfile({
      firstName: profileForm.firstName.trim(),
      lastName: profileForm.lastName.trim(),
      phoneNumber: stripPhoneFormatting(profileForm.phoneNumber) || null,
    });

    if (result.success) {
      toast.success('Profile updated successfully');
      setIsEditingProfile(false);
      // Refresh user data in context
      await oathGetCurrentUser();
      // Force a re-render by refreshing session (which updates user in context)
      await refreshSession();
    } else {
      setProfileError(result.error || 'Failed to update profile');
    }

    setSavingProfile(false);
  };

  return (
    <OathPageWrapper maxWidth="max-w-7xl">
      {/* Page Header */}
      <OathHeaderCard
        title="Profile"
        subtitle="Manage your account information"
        icon={UserCircleIcon}
        compact
      />

      {/* User Info Card */}
      <OathContentCard className="mb-6">
        {isEditingProfile ? (
          // Edit Mode
          <div className="space-y-4">
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-semibold text-gray-900 dark:text-white">Edit Profile</h3>
              <button
                onClick={handleCancelEdit}
                className="p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                aria-label="Cancel editing"
              >
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>

            {profileError && (
              <div className="p-3 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-xl text-sm text-red-600 dark:text-red-400">
                {profileError}
              </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                  First Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="firstName"
                  value={profileForm.firstName}
                  onChange={handleProfileChange}
                  className="w-full px-4 py-3 sm:py-2.5 border border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-primary/20 focus:border-primary text-base sm:text-sm transition-all bg-white dark:bg-gray-700 dark:text-white"
                  placeholder="First name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                  Last Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="lastName"
                  value={profileForm.lastName}
                  onChange={handleProfileChange}
                  className="w-full px-4 py-3 sm:py-2.5 border border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-primary/20 focus:border-primary text-base sm:text-sm transition-all bg-white dark:bg-gray-700 dark:text-white"
                  placeholder="Last name"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                Phone Number
              </label>
              <input
                type="tel"
                name="phoneNumber"
                value={profileForm.phoneNumber}
                onChange={handleProfileChange}
                className="w-full px-4 py-3 sm:py-2.5 border border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-primary/20 focus:border-primary text-base sm:text-sm transition-all bg-white dark:bg-gray-700 dark:text-white"
                placeholder="(555) 555-5555"
              />
            </div>

            <div className="flex justify-end gap-3 pt-2">
              <OathButton
                type="button"
                variant="outline"
                onClick={handleCancelEdit}
                disabled={savingProfile}
              >
                Cancel
              </OathButton>
              <OathButton
                type="button"
                onClick={handleSaveProfile}
                disabled={savingProfile}
                loading={savingProfile}
              >
                <CheckIcon className="w-4 h-4" />
                {savingProfile ? 'Saving...' : 'Save Changes'}
              </OathButton>
            </div>
          </div>
        ) : (
          // View Mode
          <div className="flex flex-col sm:flex-row sm:items-start gap-4">
            {/* Avatar */}
            <div className="flex justify-center sm:justify-start">
              <div className="w-20 h-20 sm:w-16 sm:h-16 bg-primary-100 dark:bg-primary-900/30 rounded-full flex items-center justify-center flex-shrink-0">
                <UserCircleIcon className="w-12 h-12 sm:w-10 sm:h-10 text-primary dark:text-primary-light" />
              </div>
            </div>

            {/* User Details */}
            <div className="flex-1 min-w-0 text-center sm:text-left">
              <div className="flex items-center justify-center sm:justify-between gap-2">
                <h2 className="text-lg sm:text-xl font-semibold text-gray-900 dark:text-white truncate">
                  {user?.firstName} {user?.lastName}
                </h2>
                <button
                  onClick={handleEditProfile}
                  className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 text-sm text-primary dark:text-primary-light hover:bg-primary-50 dark:hover:bg-primary-900/30 rounded-lg transition-colors"
                >
                  <PencilIcon className="w-4 h-4" />
                  Edit
                </button>
              </div>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">@{user?.userName}</p>

              <div className="mt-3 sm:mt-4 space-y-2">
                <div className="flex items-center justify-center sm:justify-start gap-2 text-sm text-gray-600 dark:text-gray-300">
                  <EnvelopeIcon className="w-4 h-4 text-gray-400 flex-shrink-0" />
                  <span className="truncate">{user?.email || 'No email set'}</span>
                </div>
                <div className="flex items-center justify-center sm:justify-start gap-2 text-sm text-gray-600 dark:text-gray-300">
                  <PhoneIcon className="w-4 h-4 text-gray-400 flex-shrink-0" />
                  <span>{user?.phoneNumber ? formatPhoneNumber(user.phoneNumber) : 'No phone set'}</span>
                </div>
              </div>

              {/* Mobile Edit Button */}
              <button
                onClick={handleEditProfile}
                className="sm:hidden mt-4 flex items-center justify-center gap-1.5 w-full px-4 py-2.5 text-sm font-medium text-primary dark:text-primary-light border border-primary-200 dark:border-primary-800 hover:bg-primary-50 dark:hover:bg-primary-900/30 rounded-xl transition-colors"
              >
                <PencilIcon className="w-4 h-4" />
                Edit Profile
              </button>
            </div>
          </div>
        )}
      </OathContentCard>

      {/* Change Password Section */}
      <OathContentCard className="mb-6">
        <button
          onClick={() => setShowChangePassword(!showChangePassword)}
          className="w-full flex items-center justify-between py-1 -my-1 active:bg-gray-50 dark:active:bg-gray-700 rounded-lg transition-colors"
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-orange-100 dark:bg-orange-900/30 rounded-full flex items-center justify-center flex-shrink-0">
              <KeyIcon className="w-5 h-5 text-orange-600 dark:text-orange-400" />
            </div>
            <div className="text-left">
              <h3 className="font-semibold text-gray-900 dark:text-white">Password</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400 hidden sm:block">Update your password</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-primary dark:text-primary-light hidden sm:inline">
              {showChangePassword ? 'Cancel' : 'Change'}
            </span>
            {showChangePassword ? (
              <ChevronUpIcon className="w-5 h-5 text-gray-400" />
            ) : (
              <ChevronDownIcon className="w-5 h-5 text-gray-400" />
            )}
          </div>
        </button>

        {showChangePassword && (
          <form onSubmit={handleSubmitPasswordChange} className="mt-4 sm:mt-6 space-y-4">
            {passwordError && (
              <div className="p-3 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-xl text-sm text-red-600 dark:text-red-400">
                {passwordError}
              </div>
            )}

            {/* Current Password */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                Current Password
              </label>
              <div className="relative">
                <input
                  type={showPasswords.current ? 'text' : 'password'}
                  name="currentPassword"
                  value={passwordForm.currentPassword}
                  onChange={handlePasswordChange}
                  autoComplete="current-password"
                  className="w-full px-4 py-3 sm:py-2.5 border border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-primary/20 focus:border-primary pr-12 text-base sm:text-sm transition-all bg-white dark:bg-gray-700 dark:text-white"
                  placeholder="Enter current password"
                />
                <button
                  type="button"
                  onClick={() => togglePasswordVisibility('current')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 active:text-gray-800 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
                  aria-label={showPasswords.current ? 'Hide password' : 'Show password'}
                >
                  {showPasswords.current ? (
                    <EyeSlashIcon className="w-5 h-5" />
                  ) : (
                    <EyeIcon className="w-5 h-5" />
                  )}
                </button>
              </div>
            </div>

            {/* New Password */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                New Password
              </label>
              <div className="relative">
                <input
                  type={showPasswords.new ? 'text' : 'password'}
                  name="newPassword"
                  value={passwordForm.newPassword}
                  onChange={handlePasswordChange}
                  autoComplete="new-password"
                  className="w-full px-4 py-3 sm:py-2.5 border border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-primary/20 focus:border-primary pr-12 text-base sm:text-sm transition-all bg-white dark:bg-gray-700 dark:text-white"
                  placeholder="Enter new password"
                />
                <button
                  type="button"
                  onClick={() => togglePasswordVisibility('new')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 active:text-gray-800 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
                  aria-label={showPasswords.new ? 'Hide password' : 'Show password'}
                >
                  {showPasswords.new ? (
                    <EyeSlashIcon className="w-5 h-5" />
                  ) : (
                    <EyeIcon className="w-5 h-5" />
                  )}
                </button>
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1.5">
                Must be at least 8 characters
              </p>
            </div>

            {/* Confirm Password */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                Confirm New Password
              </label>
              <div className="relative">
                <input
                  type={showPasswords.confirm ? 'text' : 'password'}
                  name="confirmPassword"
                  value={passwordForm.confirmPassword}
                  onChange={handlePasswordChange}
                  autoComplete="new-password"
                  className="w-full px-4 py-3 sm:py-2.5 border border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-primary/20 focus:border-primary pr-12 text-base sm:text-sm transition-all bg-white dark:bg-gray-700 dark:text-white"
                  placeholder="Confirm new password"
                />
                <button
                  type="button"
                  onClick={() => togglePasswordVisibility('confirm')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 active:text-gray-800 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
                  aria-label={showPasswords.confirm ? 'Hide password' : 'Show password'}
                >
                  {showPasswords.confirm ? (
                    <EyeSlashIcon className="w-5 h-5" />
                  ) : (
                    <EyeIcon className="w-5 h-5" />
                  )}
                </button>
              </div>
            </div>

            {/* Submit Button */}
            <div className="pt-2">
              <OathButton
                type="submit"
                disabled={changingPassword}
                loading={changingPassword}
                className="w-full sm:w-auto"
              >
                {changingPassword ? 'Updating...' : 'Update Password'}
              </OathButton>
            </div>
          </form>
        )}
      </OathContentCard>

      {/* Agencies Section */}
      <OathContentCard>
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 bg-primary-100 dark:bg-primary-900/30 rounded-full flex items-center justify-center flex-shrink-0">
            <BuildingOfficeIcon className="w-5 h-5 text-primary dark:text-primary-light" />
          </div>
          <div>
            <h3 className="font-semibold text-gray-900 dark:text-white">Your Agencies</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Agencies you have access to
            </p>
          </div>
        </div>

        {loadingAgencies ? (
          <div className="py-8 text-center">
            <OathDualSpinner size="md" className="mx-auto" />
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-3">Loading agencies...</p>
          </div>
        ) : agencies.length === 0 ? (
          <div className="py-8 text-center text-gray-500 dark:text-gray-400">
            <BuildingOfficeIcon className="w-12 h-12 mx-auto text-gray-300 dark:text-gray-600 mb-2" />
            <p>No agencies found</p>
          </div>
        ) : (
          <div className="space-y-3">
            {agencies.map((agency) => (
              <AgencyCard key={agency.agencyId} agency={agency} />
            ))}
          </div>
        )}
      </OathContentCard>
    </OathPageWrapper>
  );
};

// Agency Card Component - Compact read-only list item
const AgencyCard = ({ agency }) => {
  const displayName = agency.dbaAgencyName || agency.legalAgencyName || agency.agencyName || 'Unknown Agency';

  return (
    <div className="flex items-center gap-3 px-3 py-2.5 rounded-lg bg-gray-50 dark:bg-gray-700/50">
      <div className="w-8 h-8 rounded-lg bg-primary/10 dark:bg-primary/20 flex items-center justify-center flex-shrink-0">
        <span className="text-primary dark:text-primary-light font-semibold text-xs">
          {displayName.charAt(0).toUpperCase()}
        </span>
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-medium text-sm text-gray-900 dark:text-white truncate">{displayName}</p>
      </div>
      <span className="text-xs font-mono text-gray-400 dark:text-gray-500 flex-shrink-0">#{agency.agencyId}</span>
    </div>
  );
};

export default ProfilePage;
